// probably timing problem with lit: https://github.com/lit/lit/blob/7453e365000e6a289c139cf7e175a4742296333d/packages/reactive-element/src/css-tag.ts#L10
// if lit is loaded before style sheet it cannot handle CSSStyleSheets from AdornisCSS
import { BaseQLClient } from '@adornis/baseql/client/baseqlClient.js';
import '@adornis/chemistry/elements/components/x-icon.css';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import '@adornis/fonts/fontSrc/lato.css';
import { initApp } from '@adornis/functional-lit/init-app.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useLocalStorage } from '@adornis/functional-lit/use-local-storage.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { mailRouter } from '@adornis/mail-client/mail-router.js';
import '@adornis/popover/x-dropdown.css';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { routingStateSignal } from '@adornis/router/client/routing-state-signal.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { authenticationGuard } from '@adornis/user-forms/authentication-guard.js';
import { functionalLogin } from '@adornis/user-forms/function-login.js';
import '@adornis/user-forms/x-password-reset.js';
import {
  currentUser,
  isImpersonating,
  subscribeCurrentUser,
  unsubscribeCurrentUser,
} from '@adornis/users/client/currentUser.js';
import { logout } from '@adornis/users/client/loginFunctions.js';
import { downGradeSession } from '@adornis/users/db/a-user.js';
import 'construct-style-sheets-polyfill'; // THIS HAS TO BE THE FIRST IMPORT FOR FIREFOX TO WORK CORRECTLY
import { html, nothing } from 'lit';
import versionInfo from '../../../../version.json' with { type: 'json' };
import '../api/index.js';
import { mayIManageUsers } from '../features/user-management/api/mayIManageUsers.js';
import { CurrentUserBadge } from './components/CurrentUserBadge.js';
import { CustomMailOverview } from './components/CustomMailOverview.js';
import { ImpersonationBanner } from './components/ImpersonationBanner.js';
import { MailExtractionView } from './components/MailExtractionView.js';
import { NotificationsBadge } from './components/NotificationsBadge.js';
import { NotificationsPage } from './components/NotificationsPage.js';
import { SettingsPage } from './components/SettingsPage.js';
import { StatisticsPage } from './components/StatisticsPage.js';
import { UsersPage } from './components/UsersPage.js';
import './tailwind.css' with { type: 'tailwind' };

// The chemistry design system defaults its body font to 'Maven Pro', which this
// app does not bundle — so shadow-DOM components (x-text, the data-view empty
// state, dialogs like XNativeDialog.waitFor) fall back to a serif font. Point it
// at Lato (the font we actually load and already use for headings) so all
// chemistry text matches the rest of the UI. Done before the first render.
DesignSystem.themes.default.fontFamily = 'Lato';
DesignSystem.themes.default.fontFamilyHeading = 'Lato';

BaseQLClient.initDefault();
void subscribeCurrentUser();

// A deactivated account drops out of the currentUser subscription; without tearing it down
// here, reactivating would log the user straight back in against a session that has no user.
let hadUser = false;
currentUser().subscribe(user => {
  if (user) {
    hadUser = true;
    return;
  }
  // undefined means "still loading"; only null is a definitive answer. logout() is no use
  // here — it throws NotLoggedInError once currentUser is already null.
  if (user === undefined || !hadUser) return;
  hadUser = false;
  unsubscribeCurrentUser();
  void downGradeSession()().catch(() => undefined);
});

clientTranslator.mergeDictionary({
  field: {
    subject: ['Betreff', 'Subject'],
    receivedAt: ['Empfangen am', 'Received at'],
    evaluation: ['Bewertung', 'Evaluation'],
    createdAt: ['Erstellt am', 'Created at'],
  },
});

/** Where invitation and password links point (see getPasswordResetLink server-side). */
const PASSWORD_RESET_PATH = '/passwort-setzen';

/** <x-password-reset> reads the `token` query param itself — no app-side password handling. */
const PasswordResetPage = () => html`
  <div class="min-h-screen bg-base-200 flex items-center justify-center p-4">
    <div class="card w-full max-w-md bg-base-100 shadow-xl border border-base-300">
      <div class="card-body p-8">
        <div class="text-center mb-6">
          <div class="text-3xl font-extrabold tracking-tight text-neutral">
            Hoberg<span class="text-primary">&</span>Driesch
          </div>
          <p class="text-base text-base-content/60 mt-2">Lege hier dein Passwort fest.</p>
        </div>
        <x-password-reset></x-password-reset>
      </div>
    </div>
  </div>
`;

// Master–detail: the request list lives permanently in the app shell (see
// HobergUndDrieschApp), so the overview route only fills the detail pane with a guiding
// empty state, and the detail route renders the selected request. The mail body itself is
// no longer a permanent column — it's a collapsible section inside the detail view.
// We keep a reference to the sub-router so the shell can read the selected mailID
// reactively from its routing state (mailSubRouter.variables) — no window.location.
const mailSubRouter = mailRouter({
  mailOverview: () => html`
    <div class="h-full flex flex-col items-center justify-center text-center gap-3 text-base-content/40 p-8">
      <x-icon class="text-5xl">mark_email_unread</x-icon>
      <div class="text-base font-medium text-base-content/60">Wähle links eine Anfrage</div>
      <div class="text-sm max-w-xs">Die Details, Firma und Produkte erscheinen hier rechts.</div>
    </div>
  `,
  mailDetail: options => html`
    <div class="h-full min-h-0 min-w-0">${MailExtractionView({ mailID: options.mailID ?? '' })}</div>
  `,
});

// Create router for the application
export const router = makeRouter({
  statistics: {
    path: '/statistics',
    render: () => StatisticsPage(),
  },
  settings: {
    path: '/settings',
    render: () => SettingsPage(),
  },
  notifications: {
    path: '/rueckfragen',
    render: () => NotificationsPage(),
  },
  users: {
    path: '/users',
    render: () => UsersPage(),
  },
  // Registered so the router finds a match; AppRoot renders it outside the auth guard.
  passwordReset: {
    path: PASSWORD_RESET_PATH,
    render: () => PasswordResetPage(),
  },
  root: {
    path: '/mails{/*}?',
    subRouter: mailSubRouter,
  },
  catchAll: {
    path: '/',
    enter: () => {
      goTo('/mails', { replaceState: true });
      return false;
    },
    render: () => html``,
  },
  // The "Testdaten" tab is gone. The router throws when no route matches (assertExactlyOne),
  // so keep a redirect for open tabs and bookmarks pointing at /test-set. Droppable later.
  legacyTestSet: {
    path: '/test-set{/*}?',
    enter: () => {
      goTo('/mails', { replaceState: true });
      return false;
    },
    render: () => html``,
  },
}).init();

const LoginPage = () => html`
  <div
    class="min-h-screen bg-base-200 flex items-center justify-center p-4"
  >
    <div class="card w-full max-w-md bg-base-100 shadow-xl border border-base-300">
      <div class="card-body p-8">
        <div class="text-center mb-8">
          <div class="inline-flex items-center justify-center mb-4">
            <div class="text-5xl font-extrabold tracking-tight text-neutral">
              Hoberg<span class="text-primary">&</span>Driesch
            </div>
          </div>
          <p class="text-base text-base-content/60 mt-2">Logge dich ein um auf das Dashboard zuzugreifen.</p>
        </div>
        <div class="flex flex-col items-center justify-center">${functionalLogin({})}</div>
      </div>
    </div>
  </div>
`;

const navItemClass = (active: boolean, collapsed: boolean) =>
  `flex items-center gap-3 ${collapsed ? 'lg:justify-center lg:gap-0' : ''} ${
    active ? 'bg-primary/10 text-primary font-medium' : ''
  }`;

const HobergUndDrieschApp = stateful(() => {
  const activeRoute = router.currentRoute.value.route.name;
  // Convenience only — the operations re-check server-side; re-asked when impersonation swaps the account.
  const impersonating = useObservable(() => isImpersonating, []);
  const { value: mayManageUsers } = useQuery(() => mayIManageUsers()(), [impersonating]);
  // On the mails route the request list is a permanent master column next to the detail
  // outlet, so navigating between requests never replaces the whole screen. The selected
  // request comes from the sub-router's reactive routing state (its `detail` route param),
  // so reading it here re-renders the list and keeps the active row in sync.
  const onMails = activeRoute === 'root';
  // Only ask the sub-router while we are actually on its route: leaving /mails makes the root
  // router deinit() it, and reading currentRoute on a deinitialised router throws
  // ("router not initialized"), which would abort the whole shell render — the screen then
  // freezes on the previous view and /statistics + /settings become unreachable.
  const selectedMailID =
    onMails && mailSubRouter.currentRoute.value.route.name === 'detail'
      ? (mailSubRouter.variables.value.mailID ?? null)
      : null;

  // Desktop sidebar collapse (persisted): on lg the sidebar is a static flex column that
  // shrinks to a narrow icon rail (w-52 → w-16) — labels, brand, user e-mail and version hide,
  // only icons remain. On mobile it is an off-canvas overlay toggled by `mobileOpen`.
  const [collapsed, setCollapsed] = useLocalStorage<boolean>('hd-sidebar-collapsed', false);
  const [mobileOpen, setMobileOpen] = useState(false);

  return html`
    <div class="flex flex-col h-screen overflow-hidden bg-base-100">
      ${ImpersonationBanner()}
      <div class="flex flex-1 min-h-0">
        <!-- Sidebar: static flex column on lg, off-canvas overlay on mobile. A plain flex item
             reflows the content reliably when the rail width changes (no drawer-grid jank). -->
        <aside
          class="fixed inset-y-0 left-0 z-50 flex w-52 flex-col overflow-hidden bg-base-200 border-r border-base-300
                 transform transition-transform duration-200 lg:static lg:z-auto lg:translate-x-0 lg:transition-none
                 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} ${collapsed ? 'lg:w-16' : 'lg:w-52'}"
        >
          <div class="flex items-center gap-2 px-4 py-4 border-b border-base-300 ${collapsed ? 'lg:px-0 lg:justify-center' : ''}">
            <a href="/mails" class="min-w-0 flex-1 ${collapsed ? 'lg:hidden' : ''}" @click=${() => setMobileOpen(false)}>
              <div class="text-base font-semibold leading-tight truncate">Hoberg & Driesch</div>
              <div class="text-xs text-base-content/60 truncate">Quotation-Request-Handler</div>
            </a>
            <button
              class="btn btn-ghost btn-sm btn-square shrink-0 hidden lg:inline-flex"
              title=${collapsed ? 'Menü ausklappen' : 'Menü einklappen'}
              @click=${() => setCollapsed(!collapsed)}
            >
              <x-icon class="text-lg">${collapsed ? 'chevron_right' : 'chevron_left'}</x-icon>
            </button>
          </div>

          <ul class="menu menu-md gap-1 px-2 py-3 flex-1 w-full">
            <li>
              <a
                href="/mails"
                title="Mails"
                class=${navItemClass(activeRoute === 'root', collapsed)}
                @click=${() => setMobileOpen(false)}
              >
                <x-icon class="text-base">mail</x-icon>
                <span class=${collapsed ? 'lg:hidden' : ''}>Mails</span>
              </a>
            </li>
            <li>
              <a
                href="/rueckfragen"
                title="Rückfragen"
                class=${navItemClass(activeRoute === 'notifications', collapsed)}
                @click=${() => setMobileOpen(false)}
              >
                <x-icon class="text-base">notifications</x-icon>
                <span class="flex-1 ${collapsed ? 'lg:hidden' : ''}">Rückfragen</span>
                ${NotificationsBadge()}
              </a>
            </li>
            <li>
              <a
                href="/statistics"
                title="Statistiken"
                class=${navItemClass(activeRoute === 'statistics', collapsed)}
                @click=${() => setMobileOpen(false)}
              >
                <x-icon class="text-base">bar_chart</x-icon>
                <span class=${collapsed ? 'lg:hidden' : ''}>Statistiken</span>
              </a>
            </li>
            ${mayManageUsers
              ? html`
                  <li>
                    <a
                      href="/users"
                      title="Benutzer"
                      class=${navItemClass(activeRoute === 'users', collapsed)}
                      @click=${() => setMobileOpen(false)}
                    >
                      <x-icon class="text-base">group</x-icon>
                      <span class=${collapsed ? 'lg:hidden' : ''}>Benutzer</span>
                    </a>
                  </li>
                `
              : nothing}
            <li>
              <a
                href="/settings"
                title="Einstellungen"
                class=${navItemClass(activeRoute === 'settings', collapsed)}
                @click=${() => setMobileOpen(false)}
              >
                <x-icon class="text-base">settings</x-icon>
                <span class=${collapsed ? 'lg:hidden' : ''}>Einstellungen</span>
              </a>
            </li>
          </ul>

          <div class="border-t border-base-300 px-3 py-3 flex flex-col gap-2 ${collapsed ? 'lg:items-center lg:px-2' : ''}">
            <div class="flex items-center gap-2 ${collapsed ? 'lg:hidden' : ''}">${CurrentUserBadge()}</div>
            <button
              class="btn btn-ghost btn-sm gap-2 text-base-content/70 ${collapsed ? 'lg:btn-square lg:justify-center' : 'justify-start'}"
              title="Abmelden"
              @click=${() => logout()}
            >
              <x-icon class="text-sm">logout</x-icon>
              <span class=${collapsed ? 'lg:hidden' : ''}>Abmelden</span>
            </button>
            <span
              class="text-xs text-base-content/40 text-center pt-1 ${collapsed ? 'lg:hidden' : ''}"
              title=${`Build ${versionInfo.date}`}
            >
              v${versionInfo.release} · ${versionInfo.commit}
            </span>
          </div>
        </aside>

        <!-- Mobile backdrop -->
        ${mobileOpen
          ? html`<div class="fixed inset-0 z-40 bg-black/30 lg:hidden" @click=${() => setMobileOpen(false)}></div>`
          : nothing}

        <!-- Main column -->
        <div class="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
          <header class="lg:hidden flex items-center gap-2 bg-base-100 border-b border-base-300 px-2 min-h-12 shrink-0">
            <button class="btn btn-ghost btn-sm btn-square" title="Menü" @click=${() => setMobileOpen(true)}>
              <x-icon class="text-xl">menu</x-icon>
            </button>
            <span class="font-semibold">Hoberg & Driesch</span>
          </header>
          <main class="flex-1 min-h-0 overflow-hidden">
            ${onMails
              ? html`
                  <div class="flex h-full min-h-0">
                    <div class="w-85 shrink-0 border-r border-base-300 overflow-y-auto px-3">
                      ${CustomMailOverview({ selectedMailID })}
                    </div>
                    <div class="flex-1 min-w-0 overflow-hidden">${router.outlet.value}</div>
                  </div>
                `
              : html`<div class="h-full overflow-y-auto">${router.outlet.value}</div>`}
          </main>
        </div>
      </div>
    </div>
  `;
});

/**
 * Whoever follows an invitation link is not logged in yet, so that one route renders
 * outside the authentication guard — otherwise the login form would swallow the token.
 */
const AppRoot = stateful(() => {
  if (routingStateSignal.value.path === PASSWORD_RESET_PATH) return PasswordResetPage();
  return authenticationGuard(HobergUndDrieschApp, LoginPage);
});

void initApp(() => AppRoot());
